"use client"

import { useState, useEffect } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Col, Row } from "reactstrap"
import Select from "react-select"
import AddSvg from "../../assets/Add.svg"
import { createClass, updateClass } from "../../../../../api-handle/classApi"
import { removeFile, uploadMultipleFiles, uploadSingleFile } from "../../../../../api-handle/fileApi"
import { sanitizeArabicFieldEvent } from "../../../../../Services/arabicInput"
import { getAllCoaches } from "../../../../../api-handle/coachApi"
import "./class.css"
import { X } from "lucide-react"
import { toast, ToastContainer } from "react-toastify" // Import react-toastify
import "react-toastify/dist/ReactToastify.css" // Import toastify styles

const ClassFormModal = ({ isOpen, toggle, classData, onRefresh, classTypes }) => {
  const [formData, setFormData] = useState(
    classData || {
      title: "",
      titleAr: "",
      date: "",
      price: "",
      description: "",
      descriptionAr: "",
      capacity: 0,
      coverPhoto: null,
      images: [],
      classType: "",
      coach: "",
      aim: [],
      duration: "",
      type: "",
      schedule: [],
      tempCoverPhoto: null,
      tempImages: [],
    },
  )
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [coaches, setCoaches] = useState([])
  const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001"

  // Character limits
  const limits = {
    title: 100,
    titleAr: 100,
    description: 500,
    descriptionAr: 500,
    price: 9999,
    capacity: 100,
    duration: 300,
  }

  // File size limit in bytes (300KB = 3 * 1024 * 1024 bytes)
  const MAX_FILE_SIZE = 300 * 1024; // 300 KB


  const scheduleOptions = [
    { value: "mon", label: "Monday" },
    { value: "tue", label: "Tuesday" },
    { value: "wed", label: "Wednesday" },
    { value: "thu", label: "Thursday" },
    { value: "fri", label: "Friday" },
    { value: "sat", label: "Saturday" },
    { value: "sun", label: "Sunday" },
  ]

  const aimOptions = [
    { value: "weight_loss", label: "Weight Loss" },
    { value: "muscle_building", label: "Muscle Building" },
    { value: "flexibility", label: "Flexibility" },
    { value: "stress_relief", label: "Stress Relief" },
    { value: "cardio_fitness", label: "Cardio Fitness" },
    { value: "balance_and_coordination", label: "Balance & Coordination" },
    { value: "endurance", label: "Endurance" },
    { value: "mindfulness", label: "Mindfulness" },
    { value: "rehabilitation", label: "Rehabilitation" },
    { value: "dance_fitness", label: "Dance Fitness" },
    { value: "posture_improvement", label: "Posture Improvement" },
    { value: "core_strength", label: "Core Strength" },
  ]

  // Custom Select styles with theme color
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#33bdbf" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(51, 189, 191, 0.25)" : "none",
      "&:hover": {
        borderColor: "#33bdbf",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "rgba(51, 189, 191, 0.1)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#33bdbf",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#33bdbf",
      "&:hover": {
        backgroundColor: "#33bdbf",
        color: "white",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#33bdbf" : state.isFocused ? "rgba(51, 189, 191, 0.1)" : "white",
      color: state.isSelected ? "white" : "#495057",
    }),
  }

  // Fetch all coaches
  const fetchCoaches = async () => {
    try {
      const res = await getAllCoaches()
      setCoaches(res?.coaches || [])
    } catch (error) {
      console.error("Failed to fetch coaches:", error)
    }
  }

  useEffect(() => {
    fetchCoaches()
  }, [])

  useEffect(() => {
  }, [formData])

  useEffect(() => {
    if (isOpen) {
      if (classData) {
        setFormData({
          title: classData.title || "",
          titleAr: classData.titleAr || "",
          date: classData.date || "",
          price: classData.price || "",
          description: classData.description || "",
          descriptionAr: classData.descriptionAr || "",
          capacity: classData.capacity || 0,
          coverPhoto: classData.coverPhoto || "",
          images: classData.images || [],
          classType: classData.classType || "",
          coach: classData.coach?._id || "",
          aim: classData.aim || [],
          duration: classData.duration || "",
          type: classData.type || "",
          schedule: classData.schedule || [],
          tempCoverPhoto: null,
          tempImages: [],
        })
      } else {
        setFormData({
          title: "",
          titleAr: "",
          date: "",
          price: "",
          description: "",
          descriptionAr: "",
          capacity: 0,
          coverPhoto: null,
          images: [],
          classType: "",
          coach: "",
          aim: [],
          duration: "",
          type: "",
          schedule: [],
          tempCoverPhoto: null,
          tempImages: [],
        })
      }
      setErrors({})
      setErrorMessage("")
    }
  }, [classData, isOpen])

  // Handle Input Changes with File Size Validation
  const handleChange = (e) => {
    const { name, files } = e.target
    // Arabic fields (titleAr, descriptionAr) only accept Arabic text
    const value = sanitizeArabicFieldEvent(e)

    if (name === "coverPhoto" && files.length > 0) {
      const file = files[0]
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Cover photo exceeds 300KB limit.", {
          position: "top-right",
          autoClose: 3000,
        })
        return
      }
      setFormData({ ...formData, tempCoverPhoto: file })
    } else if (name === "images" && files.length > 0) {
      const validFiles = Array.from(files).filter((file) => {
        if (file.size > MAX_FILE_SIZE) {
          toast.error(`File "${file.name}" exceeds 300KB limit.`, {
            position: "top-right",
            autoClose: 3000,
          })
          return false
        }
        return true
      })
      setFormData({ ...formData, tempImages: [...formData.tempImages, ...validFiles] })
    } else {
      // Apply character limits
      if (name === "title" && value.length > limits.title) return
      if (name === "titleAr" && value.length > limits.titleAr) return
      if (name === "description" && value.length > limits.description) return
      if (name === "descriptionAr" && value.length > limits.descriptionAr) return

      // Apply number limits
      if (name === "price" && value !== "") {
        const numValue = Number(value)
        if (numValue < 0 || numValue > limits.price) return
      }
      if (name === "capacity" && value !== "") {
        const numValue = Number(value)
        if (numValue < 1 || numValue > limits.capacity) return
      }
      if (name === "duration" && value !== "") {
        const numValue = Number(value)
        if (numValue < 1 || numValue > limits.duration) return
      }

      // Clear schedule if type is single
      if (name === "type" && value === "single") {
        setFormData({ ...formData, [name]: value, schedule: [] })
      } else {
        setFormData({ ...formData, [name]: value })
      }
    }
    setErrors({ ...errors, [name]: "" })
  }

  // Get character count for display
  const getCharacterCount = (fieldName) => {
    const current = formData[fieldName]?.length || 0
    const max = limits[fieldName]
    return { current, max, remaining: max - current }
  }

  // Validate Form Fields
  const validate = () => {
    const newErrors = {}
    if (!formData.title) newErrors.title = "Class Title is required"
    if (!formData.titleAr) newErrors.titleAr = "Arabic Class Title is required"
    if (!formData.date) newErrors.date = "Date is required"
    if (!formData.price) newErrors.price = "Price is required"
    if (!formData.capacity) newErrors.capacity = "Capacity is required"
    if (!formData.description) newErrors.description = "Description is required"
    if (!formData.descriptionAr) newErrors.descriptionAr = "Arabic Description is required"
    if (!formData.coverPhoto && !formData.tempCoverPhoto) newErrors.coverPhoto = "Cover photo is required"
    if (formData.images.length === 0 && formData.tempImages.length === 0)
      newErrors.images = "At least one class image is required"
    if (!formData.classType) newErrors.classType = "Please select a class type"
    if (!formData.coach) newErrors.coach = "Please select a coach"
    if (!formData.aim || formData.aim.length === 0) newErrors.aim = "Please select at least one aim"
    if (!formData.duration) newErrors.duration = "Duration is required"
    if (!formData.type) newErrors.type = "Please select a type"
    if (formData.type === "schedule" && (!formData.schedule || formData.schedule.length === 0))
      newErrors.schedule = "Please select at least one day"
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle Save
  const handleSave = async () => {
    if (validate()) {
      setLoading(true)
      try {
        const updatedFormData = { ...formData }
        // Upload cover photo if present
        if (formData.tempCoverPhoto) {
          const uploadedCoverPhoto = await uploadSingleFile(formData.tempCoverPhoto)
          updatedFormData.coverPhoto = uploadedCoverPhoto.fileName
        }
        // Upload additional images if present
        if (formData.tempImages.length > 0) {
          const uploadedImages = await uploadMultipleFiles(formData.tempImages)
          updatedFormData.images = [...formData.images, ...uploadedImages]
        }

        const payload = {
          title: updatedFormData.title,
          titleAr: updatedFormData.titleAr,
          date: updatedFormData.date,
          price: updatedFormData.price,
          description: updatedFormData.description,
          descriptionAr: updatedFormData.descriptionAr,
          capacity: updatedFormData.capacity,
          coverPhoto: updatedFormData.coverPhoto,
          images: updatedFormData.images,
          classType: updatedFormData.classType,
          coach: updatedFormData.coach,
          aim: updatedFormData.aim,
          duration: updatedFormData.duration,
          type: updatedFormData.type,
          schedule: updatedFormData.schedule,
        }

        if (classData) {
          await updateClass({ ...payload, _id: classData._id })
          onRefresh()
        } else {
          await createClass(payload)
          onRefresh()
        }
        toggle()
      } catch (error) {
        console.log(error)
        toast.error(error.message)
        // setErrorMessage(error.message)
      } finally {
        setLoading(false)
      }
    }
  }

  // Handle Image Removal
  const removeImage = async (index) => {
    const fileName = formData.images[index]
    await removeFile(fileName)
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="custom-modal"
      style={{ maxWidth: "80vw" }}
      role="dialog"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalHeader toggle={toggle} className="text-dark" id="modal-title">
        {classData ? "Edit Class" : "Add New Class"}
      </ModalHeader>
      <ModalBody style={{ maxWidth: "80vw", maxHeight: "75vh", overflowY: "auto" }} id="modal-description">
        <Form role="form" aria-label="Class form">
          <Row>
            {/* Basic Information Section */}
            <Col md="6">
              <h6 className="section-header" role="heading" aria-level="3">
                Basic Information
              </h6>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="title" className="required-field">
                      Class Title (English)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="title"
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter Class Title"
                      maxLength={limits.title}
                      aria-describedby="title-help title-error"
                      aria-required="true"
                      aria-invalid={errors.title ? "true" : "false"}
                    />
                    <div className="character-count" id="title-help" aria-live="polite">
                      {getCharacterCount("title").current}/{limits.title} characters
                    </div>
                    {errors.title && (
                      <div className="text-danger small mt-1" id="title-error" role="alert">
                        {errors.title}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="titleAr" className="required-field">
                      Class Title (Arabic)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="titleAr"
                      type="text"
                      name="titleAr"
                      value={formData.titleAr}
                      onChange={handleChange}
                      placeholder="أدخل عنوان الفصل"
                      maxLength={limits.titleAr}
                      style={{ direction: "rtl" }}
                      aria-describedby="titleAr-help titleAr-error"
                      aria-required="true"
                      aria-invalid={errors.titleAr ? "true" : "false"}
                    />
                    <div className="character-count" id="titleAr-help" aria-live="polite">
                      {getCharacterCount("titleAr").current}/{limits.titleAr} characters
                    </div>
                    {errors.titleAr && (
                      <div className="text-danger small mt-1" id="titleAr-error" role="alert">
                        {errors.titleAr}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="type" className="required-field">
                  Type
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="type"
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  aria-describedby="type-error"
                  aria-required="true"
                  aria-invalid={errors.type ? "true" : "false"}
                >
                  <option value="">Select a type</option>
                  <option value="single">Single</option>
                  <option value="schedule">Schedule</option>
                </Input>
                {errors.type && (
                  <div className="text-danger small mt-1" id="type-error" role="alert">
                    {errors.type}
                  </div>
                )}
              </FormGroup>

              {formData.type === "schedule" && (
                <FormGroup>
                  <Label className="required-field">
                    Schedule
                    <span className="sr-only">Required field</span>
                  </Label>
                  <Select
                    isMulti
                    name="schedule"
                    options={scheduleOptions}
                    value={scheduleOptions.filter((option) => formData.schedule.includes(option.value))}
                    onChange={(selectedOptions) => {
                      const selectedValues = selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                      setFormData({ ...formData, schedule: selectedValues })
                      setErrors({ ...errors, schedule: "" })
                    }}
                    styles={selectStyles}
                    classNamePrefix="select"
                    placeholder="Select days"
                    aria-label="Select schedule days"
                    aria-describedby="schedule-error"
                  />
                  {errors.schedule && (
                    <div className="text-danger small mt-1" id="schedule-error" role="alert">
                      {errors.schedule}
                    </div>
                  )}
                </FormGroup>
              )}

              {formData?.type && (
                <FormGroup>
                  <Label for="date" className="required-field">
                    {formData.type === "single" ? "Date and Time" : "Time"}
                    <span className="sr-only">Required field</span>
                  </Label>
                  <Input
                    id="date"
                    type={formData.type === "single" ? "datetime-local" : "time"}
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    aria-describedby="date-error"
                    aria-required="true"
                    aria-invalid={errors.date ? "true" : "false"}
                  />
                  {errors.date && (
                    <div className="text-danger small mt-1" id="date-error" role="alert">
                      {errors.date}
                    </div>
                  )}
                </FormGroup>
              )}

              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="duration" className="required-field">
                      Duration (min)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="duration"
                      type="number"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="Duration"
                      min={1}
                      max={limits.duration}
                      aria-describedby="duration-help duration-error"
                      aria-required="true"
                      aria-invalid={errors.duration ? "true" : "false"}
                    />
                    <div className="input-help" id="duration-help">
                      Max {limits.duration} minutes
                    </div>
                    {errors.duration && (
                      <div className="text-danger small mt-1" id="duration-error" role="alert">
                        {errors.duration}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="price" className="required-field">
                      Price (KWD)
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="price"
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="Price"
                      min={0}
                      max={limits.price}
                      step="0.01"
                      aria-describedby="price-help price-error"
                      aria-required="true"
                      aria-invalid={errors.price ? "true" : "false"}
                    />
                    <div className="input-help" id="price-help">
                      Max {limits.price} KWD
                    </div>
                    {errors.price && (
                      <div className="text-danger small mt-1" id="price-error" role="alert">
                        {errors.price}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="capacity" className="required-field">
                      Capacity
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="capacity"
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      placeholder="Capacity"
                      min={1}
                      max={limits.capacity}
                      aria-describedby="capacity-help capacity-error"
                      aria-required="true"
                      aria-invalid={errors.capacity ? "true" : "false"}
                    />
                    <div className="input-help" id="capacity-help">
                      Max {limits.capacity} people
                    </div>
                    {errors.capacity && (
                      <div className="text-danger small mt-1" id="capacity-error" role="alert">
                        {errors.capacity}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </Col>

            {/* Details Section */}
            <Col md="6">
              <h6 className="section-header" role="heading" aria-level="3">
                Class Details
              </h6>

              <FormGroup>
                <Label className="required-field">
                  Aims
                  <span className="sr-only">Required field</span>
                </Label>
                <Select
                  isMulti
                  name="aim"
                  options={aimOptions}
                  value={aimOptions.filter((option) => formData.aim?.includes(option.label))}
                  onChange={(selectedOptions) => {
                    const selectedLabels = selectedOptions ? selectedOptions.map((opt) => opt.label) : []
                    setFormData({ ...formData, aim: selectedLabels })
                    setErrors({ ...errors, aim: "" })
                  }}
                  styles={selectStyles}
                  classNamePrefix="select"
                  placeholder="Select aims"
                  aria-label="Select class aims"
                  aria-describedby="aim-error"
                />
                {errors.aim && (
                  <div className="text-danger small mt-1" id="aim-error" role="alert">
                    {errors.aim}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="classType" className="required-field">
                  Class Type
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="classType"
                  type="select"
                  name="classType"
                  value={formData.classType}
                  onChange={handleChange}
                  aria-describedby="classType-error"
                  aria-required="true"
                  aria-invalid={errors.classType ? "true" : "false"}
                >
                  <option value="">Select a class type</option>
                  {classTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </Input>
                {errors.classType && (
                  <div className="text-danger small mt-1" id="classType-error" role="alert">
                    {errors.classType}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="coach" className="required-field">
                  Coach
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="coach"
                  type="select"
                  name="coach"
                  value={formData.coach}
                  onChange={handleChange}
                  aria-describedby="coach-error"
                  aria-required="true"
                  aria-invalid={errors.coach ? "true" : "false"}
                >
                  <option value="">Select a coach</option>
                  {coaches.map((coach) => (
                    <option key={coach._id} value={coach._id}>
                      {coach.name}
                    </option>
                  ))}
                </Input>
                {errors.coach && (
                  <div className="text-danger small mt-1" id="coach-error" role="alert">
                    {errors.coach}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Description Section - Full Width Row */}
          <Row className="mt-4">
            <Col md="12">
              <h6 className="section-header" role="heading" aria-level="3">
                Descriptions
              </h6>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="description" className="required-field">
                  Class Description (English)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="description"
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter Class Description"
                  maxLength={limits.description}
                  rows="4"
                  className="scrollable-textarea"
                  aria-describedby="description-help description-error"
                  aria-required="true"
                  aria-invalid={errors.description ? "true" : "false"}
                />
                <div className="character-count" id="description-help" aria-live="polite">
                  {getCharacterCount("description").current}/{limits.description} characters
                </div>
                {errors.description && (
                  <div className="text-danger small mt-1" id="description-error" role="alert">
                    {errors.description}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="descriptionAr" className="required-field">
                  Class Description (Arabic)
                  <span className="sr-only">Required field</span>
                </Label>
                <Input
                  id="descriptionAr"
                  type="textarea"
                  name="descriptionAr"
                  value={formData.descriptionAr}
                  onChange={handleChange}
                  placeholder="أدخل وصف الفصل"
                  maxLength={limits.descriptionAr}
                  rows="4"
                  className="scrollable-textarea"
                  style={{ direction: "rtl" }}
                  aria-describedby="descriptionAr-help descriptionAr-error"
                  aria-required="true"
                  aria-invalid={errors.descriptionAr ? "true" : "false"}
                />
                <div className="character-count" id="descriptionAr-help" aria-live="polite">
                  {getCharacterCount("descriptionAr").current}/{limits.descriptionAr} characters
                </div>
                {errors.descriptionAr && (
                  <div className="text-danger small mt-1" id="descriptionAr-error" role="alert">
                    {errors.descriptionAr}
                  </div>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Media Upload Section */}
          <Row className="mt-4">
            <Col md="12">
              <h6 className="section-header" role="heading" aria-level="3">
                <i className="fas fa-images me-2" aria-hidden="true"></i>
                Media Upload
              </h6>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="coverPhoto" className="required-field">
                      Upload Cover Photo
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="coverPhoto"
                      type="file"
                      name="coverPhoto"
                      onChange={handleChange}
                      accept="image/*"
                      aria-describedby="coverPhoto-help coverPhoto-error"
                      aria-required="true"
                      aria-invalid={errors.coverPhoto ? "true" : "false"}
                    />
                    <div className="input-help" id="coverPhoto-help">
                      Accepted formats: JPG, PNG, GIF (Max 300KB)
                    </div>
                    {errors.coverPhoto && (
                      <div className="text-danger small mt-1" id="coverPhoto-error" role="alert">
                        {errors.coverPhoto}
                      </div>
                    )}
                    {(formData.tempCoverPhoto || formData.coverPhoto) && (
                      <div className="mt-2">
                        <img
                          src={
                            formData.tempCoverPhoto
                              ? URL.createObjectURL(formData.tempCoverPhoto)
                              : formData.coverPhoto
                                ? `${FILE_BASE_URL}/${formData.coverPhoto}`
                                : AddSvg
                          }
                          alt="Cover Photo Preview"
                          className="img-thumbnail cover-preview"
                        />
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label for="images" className="required-field">
                      Upload Class Images
                      <span className="sr-only">Required field</span>
                    </Label>
                    <Input
                      id="images"
                      type="file"
                      name="images"
                      multiple
                      onChange={handleChange}
                      accept="image/*"
                      aria-describedby="images-help images-error"
                      aria-required="true"
                      aria-invalid={errors.images ? "true" : "false"}
                    />
                    <div className="input-help" id="images-help">
                      Select multiple images (Max 300KB each)
                    </div>
                    {errors.images && (
                      <div className="text-danger small mt-1" id="images-error" role="alert">
                        {errors.images}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Image Preview Section */}
              {(formData.images.length > 0 || formData.tempImages.length > 0) && (
                <div className="mt-3">
                  <Label>Uploaded Images</Label>
                  <div className="image-gallery" role="group" aria-label="Uploaded class images">
                    {formData.images.map((fileName, index) => (
                      <div key={index} className="image-item">
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="btn btn-danger btn-sm remove-btn"
                          aria-label={`Remove image ${index + 1}`}
                          title="Remove image"
                        >
                          <X size={20} style={{ position: "absolute" }} className="absolute" />
                        </button>
                        <img
                          src={`${FILE_BASE_URL}/${fileName}`}
                          alt={`Class image ${index + 1}`}
                          className="img-thumbnail"
                        />
                      </div>
                    ))}
                    {formData.tempImages.map((file, index) => (
                      <div key={`temp-${index}`} className="image-item">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              tempImages: formData.tempImages.filter((_, i) => i !== index),
                            })
                          }}
                          className="btn btn-danger btn-sm remove-btn"
                          aria-label={`Remove preview image ${index + 1}`}
                          title="Remove preview image"
                        >
                          <X size={20} style={{ position: "absolute" }} className="absolute" />
                        </button>
                        <img
                          src={URL.createObjectURL(file) || "/placeholder.svg"}
                          alt={`Class preview image ${index + 1}`}
                          className="img-thumbnail"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {errorMessage && (
            <Row className="mt-3">
              <Col md="12">
                <div className="alert alert-danger" role="alert" aria-live="assertive">
                  <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
                  {errorMessage}
                </div>
              </Col>
            </Row>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} aria-label="Cancel and close form">
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSave}
          disabled={loading}
          aria-label={loading ? "Saving class data" : "Save class data"}
          className="save-btn"
        >
          {loading ? (
            <>
              Saving...
            </>
          ) : (
            <>
              Save Class
            </>
          )}
        </Button>
      </ModalFooter>
      <ToastContainer /> {/* Add ToastContainer to render toasts */}
    </Modal>
  )
}

export default ClassFormModal